import axios from "axios";

export const ROUNDING_AI_PROVIDER = "vertex_gemini";

const useMockRoundingNotes =
  process.env.REACT_APP_ROUNDING_NOTES_MOCK === "true";
const localAiEnotesBaseUrl =
  process.env.REACT_APP_AI_ENOTES_LOCAL_URL || "http://localhost:8088";
const isLocalhost =
  typeof window !== "undefined" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname);
const useLocalRoundingSeed =
  isLocalhost && process.env.REACT_APP_ROUNDING_NOTES_SEED !== "false";

export const isLocalRoundingSeedEnabled = () => useLocalRoundingSeed;

export const isRoundingNotesEnabled = (provider = {}) =>
  useMockRoundingNotes ||
  useLocalRoundingSeed ||
  provider?.multidisciplinaryRoundingNotesEnable === true ||
  provider?.multidisciplinaryRoundingNotesEnable === "true";

let mockProcessed = false;
let mockAiResult = null;

const wait = (ms = 250) => new Promise((resolve) => setTimeout(resolve, ms));

const mockPatients = [
  {
    patientId: 101001,
    id: 101001,
    consultId: 501001,
    name: "Maya Srinivasan",
    fname: "Maya",
    lname: "Srinivasan",
    bedNumber: "ICU Bed 7",
    wardName: "ICU",
    hospitalId: 9001,
    status: "Active",
    diagnosis: "Septic shock, improving",
  },
  {
    patientId: 101002,
    id: 101002,
    consultId: 501002,
    name: "Robert Hayes",
    fname: "Robert",
    lname: "Hayes",
    bedNumber: "ICU Bed 8",
    wardName: "ICU",
    hospitalId: 9001,
    status: "Active",
    diagnosis: "Acute hypoxemic respiratory failure",
  },
  {
    patientId: 101003,
    id: 101003,
    consultId: 501003,
    name: "Elena Morales",
    fname: "Elena",
    lname: "Morales",
    bedNumber: "ICU Bed 9",
    wardName: "ICU",
    hospitalId: 9001,
    status: "Active",
    diagnosis: "DKA, resolving",
  },
];

const mockTranscript = [
  "[1 00:00-00:08] Bedside Nurse: We are starting with Maya Srinivasan in ICU bed 7.",
  "[2 00:08-00:22] Bedside Nurse: She was afebrile overnight, norepinephrine is now off, and urine output improved.",
  "[3 00:22-00:34] Pharmacist: She remains on cefepime, and cultures are still pending.",
  "[4 00:34-00:49] Remote Physician: Continue cefepime today, follow cultures, and keep monitoring urine output.",
  "[5 00:49-00:57] Bedside Nurse: Next is Robert Hayes, ICU bed 8.",
  "[6 00:57-01:12] Respiratory Therapist: He tolerated oxygen wean from six liters to four liters nasal cannula overnight.",
  "[7 01:12-01:26] Bedside Nurse: He still has mild increased work of breathing when turning.",
  "[8 01:26-01:40] Remote Physician: Continue oxygen wean as tolerated and repeat chest x-ray if work of breathing worsens.",
  "[9 01:40-01:50] Bedside Nurse: Moving to Elena Morales in ICU bed 9.",
  "[10 01:50-02:05] Bedside Nurse: Her anion gap is closed, insulin drip is off, and she tolerated clear liquids.",
  "[11 02:05-02:20] Remote Physician: Transition to subcutaneous insulin protocol and advance diet as tolerated.",
  "[12 02:20-02:31] Unknown: The family asked about tomorrow afternoon, but we did not confirm which patient that was for.",
].join("\n");

const mockSession = () => ({
  id: 7001,
  roundingNoteId: 7001,
  zoomMeetingId: 99001122,
  hospitalId: 9001,
  title: "ICU Multidisciplinary Rounds",
  hospitalName: "Local Test Hospital",
  wardName: "ICU",
  startedAt: Date.now() - 45 * 60 * 1000,
  status: mockProcessed ? "needs_review" : "transcript_ready",
});

const isLocalSeedSession = ({ roundingNoteId, zoomMeetingId } = {}) =>
  useLocalRoundingSeed &&
  (Number(roundingNoteId) === 7001 || Number(zoomMeetingId) === 99001122);

const mockDetailResponse = () => ({
  status: true,
  session: mockAiResult?.session || mockSession(),
  patients: mockPatients,
  patientRoster: mockPatients,
  patientSegments: mockAiResult?.patientSegments || [],
  unmatchedSegments: mockAiResult?.unmatchedSegments || [],
  transcript: mockTranscript,
});

const buildMockProcessingPayload = ({ roundingNoteId, zoomMeetingId }) => ({
  roundingNoteId,
  zoomMeetingId,
  hospitalId: 9001,
  hospitalName: "Local Test Hospital",
  wardName: "ICU",
  title: "ICU Multidisciplinary Rounds",
  transcript: mockTranscript,
  patients: mockPatients,
});

const emptySessionsResponse = (errorMessage = "") => ({
  status: false,
  sessions: [],
  errorMessage,
});

const emptyDetailResponse = (errorMessage = "") => ({
  status: false,
  session: null,
  patientSegments: [],
  unmatchedSegments: [],
  transcript: "",
  errorMessage,
});

export const getRoundingNoteSessions = async ({
  hospitalId,
  providerId,
  wardName,
  fromTime,
  toTime,
} = {}) => {
  if (useMockRoundingNotes) {
    await wait();
    return {
      status: true,
      sessions: [mockSession()],
    };
  }
  try {
    const { data } = await axios.post(
      "api/commonEndpoints/v1/getMultidisciplinaryRoundingNotes",
      {
        hospitalId,
        providerId,
        wardName,
        fromTime,
        toTime,
        aiProvider: ROUNDING_AI_PROVIDER,
      }
    );
    const response = data || emptySessionsResponse();
    const sessions =
      response.sessions ||
      response.roundingSessions ||
      response.roundingNotes ||
      response.data ||
      [];
    if (useLocalRoundingSeed && Array.isArray(sessions) && !sessions.length) {
      return {
        status: true,
        sessions: [mockSession()],
        seededLocalRound: true,
      };
    }
    return response;
  } catch (error) {
    console.log(error);
    if (useLocalRoundingSeed) {
      return {
        status: true,
        sessions: [mockSession()],
        seededLocalRound: true,
      };
    }
    return emptySessionsResponse(
      "Multidisciplinary rounding notes are unavailable."
    );
  }
};

export const getRoundingNoteDetails = async ({
  roundingNoteId,
  zoomMeetingId,
  hospitalId,
  providerId,
}) => {
  if (
    useMockRoundingNotes ||
    isLocalSeedSession({ roundingNoteId, zoomMeetingId })
  ) {
    await wait();
    return mockDetailResponse();
  }
  try {
    const { data } = await axios.post(
      "api/commonEndpoints/v1/getMultidisciplinaryRoundingNoteDetails",
      {
        roundingNoteId,
        zoomMeetingId,
        hospitalId,
        providerId,
        aiProvider: ROUNDING_AI_PROVIDER,
      }
    );
    return data || emptyDetailResponse();
  } catch (error) {
    console.log(error);
    return emptyDetailResponse("Unable to load rounding note details.");
  }
};

export const processRoundingTranscript = async ({
  roundingNoteId,
  zoomMeetingId,
  hospitalId,
  providerId,
}) => {
  if (
    useMockRoundingNotes ||
    isLocalSeedSession({ roundingNoteId, zoomMeetingId })
  ) {
    try {
      const { data } = await axios.post(
        `${localAiEnotesBaseUrl}/ai/processRoundingTranscript`,
        buildMockProcessingPayload({ roundingNoteId, zoomMeetingId })
      );
      if (!data?.status) {
        return {
          status: false,
          errorMessage:
            data?.errorMessage ||
            data?.error ||
            "Unable to create draft notes.",
        };
      }
      mockAiResult = {
        ...data,
        session: {
          ...mockSession(),
          ...(data.session || {}),
          status: data.session?.status || "needs_review",
        },
        patients: data.patients || mockPatients,
        patientRoster: data.patientRoster || data.patients || mockPatients,
        transcript: data.transcript || mockTranscript,
      };
      mockProcessed = true;
      return mockDetailResponse();
    } catch (error) {
      console.log(error);
      return {
        status: false,
        errorMessage:
          error?.response?.data?.error ||
          "Unable to reach local ai-enotes. Start it on port 8088 and try again.",
      };
    }
  }
  try {
    const { data } = await axios.post(
      "api/commonEndpoints/v1/processMultidisciplinaryRoundingTranscript",
      {
        roundingNoteId,
        zoomMeetingId,
        hospitalId,
        providerId,
        aiProvider: ROUNDING_AI_PROVIDER,
      }
    );
    return data;
  } catch (error) {
    console.log(error);
    return {
      status: false,
      errorMessage: "Unable to create draft rounding notes.",
    };
  }
};

const mockAssignUnmatchedSegment = ({
  segmentId,
  unmatchedSegmentIndex,
  patientId,
}) => {
  const detail = mockDetailResponse();
  const unmatchedSegments = [...(detail.unmatchedSegments || [])];
  const index =
    typeof unmatchedSegmentIndex === "number"
      ? unmatchedSegmentIndex
      : unmatchedSegments.findIndex(
          (segment) =>
            String(segment.segmentId || segment.segment_id || segment.id) ===
            String(segmentId)
        );
  if (index < 0 || !unmatchedSegments[index]) {
    return {
      status: false,
      errorMessage: "Unassigned transcript section not found.",
    };
  }
  const segment = unmatchedSegments[index];
  const patient = mockPatients.find(
    (mockPatient) => String(mockPatient.patientId) === String(patientId)
  );
  if (!patient) {
    return {
      status: false,
      errorMessage: "Assigned patient is not active in this hospital.",
    };
  }
  const transcriptText =
    segment.transcriptText ||
    segment.transcript_text ||
    segment.segmentText ||
    segment.segment_text ||
    segment.sourceText ||
    segment.source_text ||
    segment.rawText ||
    segment.raw_text ||
    segment.text ||
    segment.summaryForReview ||
    segment.summary_for_review ||
    "Transcript section from multidisciplinary rounds.";
  const patientSegments = [...(detail.patientSegments || [])];
  const existingIndex = patientSegments.findIndex(
    (patientSegment) =>
      String(patientSegment.patientId || patientSegment.patient_id) ===
      String(patientId)
  );
  const regeneratedSegment = {
    ...(existingIndex >= 0 ? patientSegments[existingIndex] : {}),
    segmentId:
      segment.segmentId || segment.segment_id || `manual-${Date.now()}`,
    patientId: patient.patientId,
    consultId: patient.consultId,
    patientName: patient.name,
    bedNumber: patient.bedNumber,
    matchedPatientDisplay: `${patient.name} - ${patient.bedNumber}`,
    segmentStartTime: segment.segmentStartTime || segment.segment_start_time,
    segmentEndTime: segment.segmentEndTime || segment.segment_end_time,
    matchConfidence: 1,
    needsHumanMatchReview: false,
    matchReason: "Assigned manually from an unassigned transcript section.",
    sourceTranscriptText: [
      existingIndex >= 0
        ? patientSegments[existingIndex].sourceTranscriptText
        : "",
      transcriptText,
    ]
      .filter(Boolean)
      .join("\n\n"),
    draftProgressNote: {
      ...(existingIndex >= 0
        ? patientSegments[existingIndex].draftProgressNote || {}
        : {}),
      overnight_events: transcriptText,
    },
  };
  if (existingIndex >= 0) {
    patientSegments[existingIndex] = regeneratedSegment;
  } else {
    patientSegments.push(regeneratedSegment);
  }
  unmatchedSegments.splice(index, 1);
  mockAiResult = {
    ...detail,
    session: {
      ...detail.session,
      status: unmatchedSegments.length ? "needs_review" : "notes_ready",
      matchedPatientCount: patientSegments.length,
      unmatchedSegmentCount: unmatchedSegments.length,
    },
    patientSegments,
    unmatchedSegments,
  };
  return mockDetailResponse();
};

export const assignRoundingSegmentToPatient = async ({
  roundingNoteId,
  zoomMeetingId,
  hospitalId,
  providerId,
  patientId,
  segmentId,
  unmatchedSegmentIndex,
}) => {
  if (
    useMockRoundingNotes ||
    isLocalSeedSession({ roundingNoteId, zoomMeetingId })
  ) {
    await wait();
    return mockAssignUnmatchedSegment({
      segmentId,
      unmatchedSegmentIndex,
      patientId,
    });
  }
  try {
    const { data } = await axios.post(
      "api/commonEndpoints/v1/assignMultidisciplinaryRoundingSegment",
      {
        roundingNoteId,
        zoomMeetingId,
        hospitalId,
        providerId,
        patientId,
        segmentId,
        unmatchedSegmentIndex,
        aiProvider: ROUNDING_AI_PROVIDER,
      }
    );
    return data;
  } catch (error) {
    console.log(error);
    return {
      status: false,
      errorMessage: "Unable to assign transcript section.",
    };
  }
};

export const saveRoundingProgressENote = async ({
  message,
  patientId,
  providerId,
  providerName,
  providerToken,
  title = "Multidisciplinary Rounds",
  time = Date.now(),
}) => {
  if (useMockRoundingNotes) {
    await wait(300);
    return {
      status: true,
      statusMessage: "OK",
      chatMessages: {
        patientId,
        providerId,
        senderId: providerId,
        message,
        subType: "Progress",
        type: "eNotes",
        title,
        time,
      },
    };
  }
  try {
    const { data } = await axios.post(
      "/api/providerEndpoints/v1/sendChatMessage",
      {
        important: false,
        message,
        patientId,
        providerId,
        senderId: providerId,
        senderName: providerName,
        subType: "Progress",
        time,
        title,
        token: providerToken,
        type: "eNotes",
        urgent: false,
      }
    );
    return data;
  } catch (error) {
    console.log(error);
    return {
      status: false,
      errorMessage: "Unable to save Progress eNote.",
    };
  }
};

export const roundingStatusLabel = (status) => {
  switch (status) {
    case "notes_ready":
      return "Draft Notes Ready";
    case "needs_review":
      return "Draft Notes Ready";
    case "transcript_ready":
      return "Transcript Available";
    case "processing":
      return "Creating Draft Notes";
    case "failed":
      return "Failed";
    case "recording_processing":
      return "Preparing Transcript";
    default:
      return "Waiting";
  }
};

export const roundingStatusTone = (status) => {
  switch (status) {
    case "notes_ready":
    case "needs_review":
      return "ready";
    case "failed":
      return "failed";
    case "processing":
    case "recording_processing":
      return "processing";
    default:
      return "waiting";
  }
};
